import { useCallback, useEffect, useState } from 'react';
import type { Club } from '@court-sync/shared';
import { clubService } from '@/services/club';
import { useClubContextStore } from '@/store/clubContext';

let cachedClubs: Club[] | null = null;
let clubsFetchPromise: Promise<Club[]> | null = null;

async function loadUserClubs(): Promise<Club[]> {
  if (cachedClubs) return cachedClubs;
  if (clubsFetchPromise) return clubsFetchPromise;

  clubsFetchPromise = clubService
    .getUserClubs()
    .then((response) => {
      const clubs = response.success && response.data ? response.data : [];
      cachedClubs = clubs;
      return clubs;
    })
    .finally(() => {
      clubsFetchPromise = null;
    });

  return clubsFetchPromise;
}

export function clearClubCache() {
  cachedClubs = null;
}

export function useCurrentClub() {
  const selectedClubId = useClubContextStore((s) => s.selectedClubId);
  const selectClubInStore = useClubContextStore((s) => s.selectClub);

  const [userClubs, setUserClubs] = useState<Club[]>(cachedClubs ?? []);
  const [currentClub, setCurrentClubState] = useState<Club | null>(() => {
    if (!cachedClubs?.length) return null;
    if (selectedClubId) {
      const match = cachedClubs.find((club) => club.id === selectedClubId);
      if (match) return match;
    }
    return cachedClubs[0] ?? null;
  });
  const [isLoading, setIsLoading] = useState(!cachedClubs);

  const syncCurrentClub = useCallback(
    (clubs: Club[]) => {
      setUserClubs(clubs);
      let resolved: Club | null = null;
      if (selectedClubId) {
        resolved = clubs.find((club) => club.id === selectedClubId) ?? null;
      }
      setCurrentClubState((prev) => {
        if (resolved) return resolved;
        if (prev && clubs.some((club) => club.id === prev.id)) return prev;
        return clubs[0] ?? null;
      });
      if (!resolved) {
        const fallback = clubs[0] ?? null;
        if (fallback && fallback.id !== selectedClubId) {
          selectClubInStore(fallback);
        }
      }
    },
    [selectedClubId, selectClubInStore]
  );

  const loadClubs = useCallback(async () => {
    setIsLoading(true);
    try {
      const clubs = await loadUserClubs();
      syncCurrentClub(clubs);
    } finally {
      setIsLoading(false);
    }
  }, [syncCurrentClub]);

  useEffect(() => {
    if (!cachedClubs) {
      void loadClubs();
    } else {
      syncCurrentClub(cachedClubs);
    }
  }, [loadClubs, syncCurrentClub]);

  const setCurrentClub = useCallback(
    (club: Club) => {
      selectClubInStore(club);
      setCurrentClubState(club);
      if (cachedClubs) {
        cachedClubs = cachedClubs.map((entry) => (entry.id === club.id ? club : entry));
      }
    },
    [selectClubInStore]
  );

  const refreshClub = useCallback(async () => {
    clearClubCache();
    await loadClubs();
  }, [loadClubs]);

  return {
    userClubs,
    currentClub,
    isLoading,
    loadClubs,
    setCurrentClub,
    refreshClub,
  };
}
